import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export type OnboardingPhase = 'idle' | 'welcome' | 'app-tour' | 'first-trip' | 'complete';

const SKIP_PATHS = ['/signin', '/signup', '/reset-password', '/beta-signin', '/beta-signup', '/unsubscribe'];
const TOUR_DONE_KEY = 'tg_daruma_app_tour_done';

export interface OnboardingState {
  phase: OnboardingPhase;
  returnToWelcome: boolean;
  isCasual: boolean;
  interests: string[];
  destinations: string[];
  startDate: string;
  endDate: string;
}

const DEFAULT_STATE: OnboardingState = {
  phase: 'idle',
  returnToWelcome: false,
  isCasual: false,
  interests: [],
  destinations: [],
  startDate: '',
  endDate: '',
};

interface OnboardingContextType {
  state: OnboardingState;
  setPhase: (phase: OnboardingPhase) => void;
  updateState: (partial: Partial<OnboardingState>) => void;
  completeOnboarding: () => Promise<void>;
  replayOnboarding: () => Promise<void>;
  launchFirstTripWizard: () => void;
}

const Ctx = createContext<OnboardingContextType>({
  state: DEFAULT_STATE,
  setPhase: () => {},
  updateState: () => {},
  completeOnboarding: async () => {},
  replayOnboarding: async () => {},
  launchFirstTripWizard: () => {},
});

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<OnboardingState>(DEFAULT_STATE);

  useEffect(() => {
    if (!user) {
      setState(DEFAULT_STATE);
      return;
    }
    if (SKIP_PATHS.includes(window.location.pathname)) return;

    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_preferences')
        .select('onboarding_completed, traveller_type, interests')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;

      const completed = !!data?.onboarding_completed;
      const tourDone = (() => {
        try { return localStorage.getItem(TOUR_DONE_KEY) === '1'; } catch { return false; }
      })();

      if (!completed) {
        setState({
          ...DEFAULT_STATE,
          phase: 'welcome',
          isCasual: data?.traveller_type === 'casual',
          interests: Array.isArray(data?.interests) ? data.interests : [],
        });
      } else if (!tourDone) {
        setState({
          ...DEFAULT_STATE,
          phase: 'app-tour',
          isCasual: data?.traveller_type === 'casual',
          interests: Array.isArray(data?.interests) ? data.interests : [],
        });
      } else {
        setState(prev => ({ ...prev, phase: 'complete' }));
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  const setPhase = useCallback((phase: OnboardingPhase) => {
    setState(prev => ({ ...prev, phase }));
  }, []);

  const updateState = useCallback((partial: Partial<OnboardingState>) => {
    setState(prev => ({ ...prev, ...partial }));
  }, []);

  const completeOnboarding = useCallback(async () => {
    if (!user) return;
    try {
      await supabase.from('user_preferences').upsert({
        user_id: user.id,
        onboarding_completed: true,
      }, { onConflict: 'user_id' });
    } catch { /* non-fatal */ }
    try { localStorage.setItem(TOUR_DONE_KEY, '1'); } catch { /* ignore */ }
    setState(prev => ({ ...prev, phase: 'complete' }));
  }, [user]);

  const replayOnboarding = useCallback(async () => {
    if (!user) return;
    let isCasual = false;
    let interests: string[] = [];
    try {
      const { data } = await supabase
        .from('user_preferences')
        .select('traveller_type, interests')
        .eq('user_id', user.id)
        .maybeSingle();
      isCasual = data?.traveller_type === 'casual';
      interests = Array.isArray(data?.interests) ? data.interests : [];
      await supabase.from('user_preferences').update({
        onboarding_completed: false,
      }).eq('user_id', user.id);
    } catch { /* non-fatal */ }
    try { localStorage.removeItem(TOUR_DONE_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem('tg_daruma_tour_done'); } catch { /* ignore */ }
    // Skip Phase 1 (welcome wizard) — the user has already chosen casual/enthusiast.
    // Jump straight to Phase 2 (spotlight tour).
    setState({ ...DEFAULT_STATE, phase: 'app-tour', isCasual, interests });
  }, [user]);

  const launchFirstTripWizard = useCallback(() => {
    setState(prev => ({ ...prev, phase: 'first-trip' }));
  }, []);

  return (
    <Ctx.Provider value={{ state, setPhase, updateState, completeOnboarding, replayOnboarding, launchFirstTripWizard }}>
      {children}
    </Ctx.Provider>
  );
}

export const useOnboarding = () => useContext(Ctx);
